/**
 * The abbreviations the product cannot avoid, and what each one stands for.
 *
 * Bulgarian tax writing is dense with them — ДОО, ОЗМ, ЗДДС — and they are not decoration: they are
 * what the НАП portal, the payment orders and an accountant's email all say, so the app has to say
 * them too. The person who most needs the product is the one who has never seen any of them.
 *
 * One dictionary feeds both answers to that. `findTerms` is what the hover layer uses to explain an
 * abbreviation where it stands; `expandTerms` is what the "full names" setting uses to write it out
 * instead. Keeping them on the same data is the point: a term explained on hover and spelled out in
 * settings must not be able to disagree about what it means.
 *
 * It lives here rather than in the locale files because the two languages do not share keys — the
 * English copy says NRA where the Bulgarian says НАП — and check-i18n rightly insists that they do.
 */

export interface Term {
  /** The official name, shown in the hint. */
  full: string;
  /** One sentence on what it means for the reader, not what the letters stand for. */
  note: string;
  /**
   * What replaces the abbreviation in running text when full names are on.
   *
   * Absent on purpose for the ones everybody says as a word — ДДС, ЕГН, НАП. Writing those out turns
   * a nav item into a paragraph and explains nothing; they keep the hint and stay as they are. Where
   * present it is the form that survives the sentences it actually appears in, which is why the laws
   * carry their definite article: every one of them follows „по“, „от“ or an article number.
   */
  inline?: string;
  /** Inserted when the abbreviation directly follows a number: „чл. 96 ЗДДС“ → „чл. 96 от Закона…“. */
  cite?: string;
  /** The glossary entry that says more, as an id under `help.entries`. */
  help?: string;
}

const bg: Record<string, Term> = {
  НАП: {
    full: 'Национална агенция за приходите',
    note: 'Държавната агенция, която събира данъците и осигуровките. Пред нея подавате декларациите си и към нея плащате.',
  },
  НОИ: {
    full: 'Национален осигурителен институт',
    note: 'Институцията, която изплаща пенсиите, болничните и обезщетенията за майчинство — тоест това, за което плащате осигуровки.',
    inline: 'Националния осигурителен институт',
  },
  ДОО: {
    full: 'Държавно обществено осигуряване',
    note: 'Осигуровките за пенсия, а по избор — и за болнични и майчинство. Плаща се отделно от здравната вноска.',
    inline: 'държавно обществено осигуряване',
    help: 'doo',
  },
  ОЗМ: {
    full: 'Общо заболяване и майчинство',
    note: 'Доброволна осигуровка за самоосигуряващите се. Без нея нямате право на болничен и на обезщетение за майчинство.',
    inline: 'общо заболяване и майчинство',
    help: 'ozm',
  },
  УПФ: {
    full: 'Универсален пенсионен фонд',
    note: 'Частен фонд, в който отива част от пенсионната вноска на родените след 1959 г.',
    inline: 'универсален пенсионен фонд',
    help: 'upf',
  },
  ДДС: {
    full: 'Данък върху добавената стойност',
    note: 'Начислява се върху продажбите на регистрираните лица. Засяга ви, след като оборотът ви мине прага за регистрация или работите с фирми от ЕС.',
  },
  ЗДДС: {
    full: 'Закон за данък върху добавената стойност',
    note: 'Законът, който определя кога се регистрирате по ДДС, какво пише във фактурата и какво се декларира всеки месец.',
    inline: 'Закона за ДДС',
    cite: 'от ',
  },
  ППЗДДС: {
    full: 'Правилник за прилагане на Закона за данък върху добавената стойност',
    note: 'Подробните правила към закона за ДДС — например как се номерират фактурите.',
    inline: 'Правилника за прилагане на Закона за ДДС',
    cite: 'от ',
  },
  ЗДДФЛ: {
    full: 'Закон за данъците върху доходите на физическите лица',
    note: 'Законът за данъка върху личните доходи: ставката, признатите разходи, облекченията и годишната декларация.',
    inline: 'Закона за данъците върху доходите на физическите лица',
    cite: 'от ',
  },
  КСО: {
    full: 'Кодекс за социално осигуряване',
    note: 'Законът за осигуровките: кой ги дължи, върху какъв доход и какво получава срещу тях.',
    inline: 'Кодекса за социално осигуряване',
    cite: 'от ',
  },
  ЗБДОО: {
    full: 'Закон за бюджета на държавното обществено осигуряване',
    note: 'Приема се всяка година и определя минималния и максималния осигурителен доход за нея.',
  },
  ЗЗП: {
    full: 'Закон за защита на потребителите',
    note: 'Законът, който ви дава правата на потребител — включително 14-дневния срок за отказ от покупка онлайн.',
    inline: 'Закона за защита на потребителите',
    cite: 'от ',
  },
  ЕГН: {
    full: 'Единен граждански номер',
    note: 'Десетцифреният номер от личната ви карта. С него НАП ви разпознава във всяка декларация и всяко плащане.',
  },
  ЕИК: {
    full: 'Единен идентификационен код',
    note: 'Номерът на фирма в Търговския регистър или в регистър БУЛСТАТ. Вписва се във всяка фактура.',
  },
  БУЛСТАТ: {
    full: 'Регистър БУЛСТАТ',
    note: 'Регистърът към Агенцията по вписванията, в който се вписват упражняващите свободна професия. Оттам идва номерът, с който издавате фактури.',
  },
  'ОКД-5': {
    full: 'Декларация за регистрация на самоосигуряващо се лице',
    note: 'Подава се в НАП до 7 дни от започване, прекъсване или прекратяване на дейността.',
  },
  ЕИП: {
    full: 'Европейско икономическо пространство',
    note: 'Държавите от ЕС заедно с Норвегия, Исландия и Лихтенщайн.',
  },
  ТЕЛК: {
    full: 'Трудово-експертна лекарска комисия',
    note: 'Комисията, която определя степента на намалена работоспособност. Решението ѝ дава право на данъчно облекчение.',
    inline: 'трудово-експертна лекарска комисия',
  },
  ПИК: {
    full: 'Персонален идентификационен код',
    note: 'Безплатен код от НАП, с който влизате в портала и подавате декларации без електронен подпис.',
    inline: 'персонален идентификационен код',
  },
  ЕООД: {
    full: 'Еднолично дружество с ограничена отговорност',
    note: 'Фирма с един собственик. Облага се по други правила от самоосигуряващото се лице.',
  },
};

/* The English copy keeps some Bulgarian abbreviations on purpose — they are what the reader will
   meet on the portal — so those are the ones an English reader can least be expected to decode. */
const en: Record<string, Term> = {
  NRA: {
    full: 'National Revenue Agency (НАП)',
    note: 'The Bulgarian tax authority. You file your returns with it and pay both tax and social contributions to it.',
    inline: 'National Revenue Agency',
  },
  НАП: {
    full: 'Национална агенция за приходите — National Revenue Agency',
    note: 'The Bulgarian tax authority. You file your returns with it and pay both tax and social contributions to it.',
  },
  NSSI: {
    full: 'National Social Security Institute (НОИ)',
    note: 'Pays out pensions, sick leave and maternity benefits — what your contributions buy.',
    inline: 'National Social Security Institute',
  },
  НОИ: {
    full: 'Национален осигурителен институт — National Social Security Institute',
    note: 'Pays out pensions, sick leave and maternity benefits — what your contributions buy.',
  },
  ДОО: {
    full: 'Държавно обществено осигуряване — state social insurance',
    note: 'Pension contributions and, if you opt in, sickness and maternity cover. Paid separately from health insurance.',
    inline: 'state social insurance (ДОО)',
    help: 'doo',
  },
  ОЗМ: {
    full: 'Общо заболяване и майчинство — sickness and maternity insurance',
    note: 'Optional for the self-insured. Without it you have no right to sick pay or maternity benefit.',
    inline: 'sickness and maternity insurance (ОЗМ)',
    help: 'ozm',
  },
  ЗДДС: {
    full: 'Закон за данък върху добавената стойност — the VAT Act',
    note: 'Decides when you must register for VAT, what an invoice has to say and what is declared each month.',
  },
  ППЗДДС: {
    full: 'Правилник за прилагане на ЗДДС — the VAT Act implementing regulations',
    note: 'The detailed rules under the VAT Act, such as how invoices are numbered.',
  },
  КСО: {
    full: 'Кодекс за социално осигуряване — the Social Insurance Code',
    note: 'The law on contributions: who owes them, on what income, and what they earn in return.',
  },
  ЕГН: {
    full: 'Единен граждански номер — national ID number',
    note: 'The ten-digit number on your ID card. The NRA identifies you by it in every return and payment.',
  },
  BULSTAT: {
    full: 'The BULSTAT register',
    note: 'The Registry Agency register where freelancers are entered. It issues the number you invoice under.',
  },
  'OKD-5': {
    full: 'Declaration of registration as a self-insured person',
    note: 'Filed with the NRA within seven days of starting, pausing or ending your activity.',
  },
  EEA: {
    full: 'European Economic Area',
    note: 'The EU member states plus Norway, Iceland and Liechtenstein.',
  },
  VIES: {
    full: 'VAT Information Exchange System',
    note: 'The EU system for cross-border VAT. The VIES declaration lists the services you supplied to EU businesses.',
  },
};

const DICTIONARIES: Record<string, Record<string, Term>> = { bg, en };

export function termsFor(language: string): Record<string, Term> {
  return DICTIONARIES[language] ?? bg;
}

const patterns = new Map<string, RegExp>();

/**
 * Longest first, so ППЗДДС is not read as a ЗДДС with a prefix, and bounded by "not a letter or a
 * digit" rather than `\b` — which JavaScript defines in terms of ASCII and so sees no boundary at
 * all between a space and a Cyrillic capital.
 */
function patternFor(language: string): RegExp {
  let pattern = patterns.get(language);

  if (!pattern) {
    const keys = Object.keys(termsFor(language))
      .sort((a, b) => b.length - a.length)
      // No hyphen in the set: under the `u` flag, `\-` outside a class is a syntax error.
      .map((key) => key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    pattern = new RegExp(`(?<![\\p{L}\\p{N}])(?:${keys.join('|')})(?![\\p{L}\\p{N}])`, 'gu');
    patterns.set(language, pattern);
  }

  return pattern;
}

export interface TermMatch {
  key: string;
  term: Term;
  start: number;
  end: number;
}

/** Every known abbreviation in `text`, in order. Case-sensitive: „НЕ“ on a button is not a term. */
export function findTerms(text: string, language: string): TermMatch[] {
  const terms = termsFor(language);
  const pattern = patternFor(language);

  // `matchAll` clones the expression along with its `lastIndex`, and this one is shared. Left where
  // `hasTerms` parked it, the search starts past the first abbreviation and silently skips it.
  pattern.lastIndex = 0;

  return [...text.matchAll(pattern)].map((match) => ({
    key: match[0],
    term: terms[match[0]],
    start: match.index,
    end: match.index + match[0].length,
  }));
}

/** Cheaper than `findTerms` for the common case of a text node with nothing in it. */
export function hasTerms(text: string, language: string): boolean {
  const pattern = patternFor(language);
  pattern.lastIndex = 0;

  const found = pattern.test(text);
  pattern.lastIndex = 0;

  return found;
}

/**
 * Writes the abbreviations out.
 *
 * Two small pieces of grammar, because a replacement that ignores them produces text nobody would
 * write: a name that opens a sentence takes a capital, and a law cited straight after an article
 * number — „чл. 96 ЗДДС“ — needs the „от“ that the abbreviation let the author skip.
 */
export function expandTerms(text: string, language: string): string {
  const matches = findTerms(text, language).filter((match) => match.term.inline);

  if (matches.length === 0) {
    return text;
  }

  let result = '';
  let cursor = 0;

  for (const { term, start, end } of matches) {
    const before = text.slice(0, start);
    let replacement = term.inline!;

    if (term.cite && /\d\s+$/.test(before)) {
      replacement = term.cite + replacement;
    } else if (/^\s*$/.test(before) || /[.!?]\s+$/.test(before)) {
      replacement = replacement.charAt(0).toLocaleUpperCase(language) + replacement.slice(1);
    }

    result += text.slice(cursor, start) + replacement;
    cursor = end;
  }

  return result + text.slice(cursor);
}
